/**
 * One-product landing helpers:
 *  - Hero gallery: swipe on mobile, thumbnails on desktop; keeps the active thumbnail
 *    scrolled into view; toggles .is-first-slide on the hero.
 *  - Sticky add-to-cart bar (shows after the hero buy box scrolls away,
 *    hides while another ATC form or the footer is on screen).
 *  - Ships-by card: dispatch date (today + N business days) and visitor country
 *    (IP via Shopify browsing context, falls back to the storefront country).
 *    Date and country name are formatted in the Weglot language (re-rendered on language change),
 *    so exclude [data-rx-delivery-date] and [data-rx-delivery-country] in Weglot.
 */

/* ---------------- Sticky ATC ---------------- */
function initStickyAtc() {
  const bar = document.querySelector('[data-rx-sticky]');
  const trigger = document.querySelector('[data-rx-sticky-trigger]');
  if (!bar || !trigger || !('IntersectionObserver' in window)) return;

  let triggerPassed = false;
  const blockersOnScreen = new Set();

  const update = () => {
    const show = triggerPassed && blockersOnScreen.size === 0;
    bar.classList.toggle('is-visible', show);
    bar.toggleAttribute('inert', !show);
    bar.setAttribute('aria-hidden', String(!show));
  };

  new IntersectionObserver(([entry]) => {
    if (!entry) return;
    triggerPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
    update();
  }).observe(trigger);

  const blockerObserver = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (entry.isIntersecting) blockersOnScreen.add(entry.target);
      else blockersOnScreen.delete(entry.target);
    }
    update();
  });

  document.querySelectorAll('form[data-rx-atc]').forEach((form) => {
    if (!bar.contains(form) && !trigger.contains(form)) blockerObserver.observe(form);
  });

  const footer = document.querySelector('footer');
  if (footer) blockerObserver.observe(footer);

  update();
}

/* ---------------- Hero gallery ---------------- */
function centerThumb(thumb) {
  const strip = thumb?.parentElement;
  if (!strip) return;
  const stripRect = strip.getBoundingClientRect();
  const thumbRect = thumb.getBoundingClientRect();
  const delta = thumbRect.left - stripRect.left - (stripRect.width - thumbRect.width) / 2;
  if (Math.abs(delta) < 2) return;
  strip.scrollBy({ left: delta, behavior: 'smooth' });
}

function initGallery() {
  document.querySelectorAll('[data-rx-gallery]').forEach((gallery) => {
    const track = gallery.querySelector('[data-rx-gallery-track]');
    const slides = track ? Array.from(track.children) : [];
    if (!track || slides.length < 2) return;

    const thumbs = Array.from(gallery.querySelectorAll('[data-rx-thumb]'));
    const count = gallery.querySelector('[data-rx-gallery-count]');
    const hero = gallery.closest('.rxh-hero');
    let current = -1;

    const setActive = (index, { scrollThumb = true } = {}) => {
      const next = Math.max(0, Math.min(slides.length - 1, index));
      if (next === current) return;
      current = next;
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === next);
        thumb.setAttribute('aria-current', i === next ? 'true' : 'false');
      });
      if (scrollThumb) centerThumb(thumbs[next]);
      if (count) count.textContent = `${next + 1} / ${slides.length}`;
      if (hero) hero.classList.toggle('is-first-slide', next === 0);
    };

    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const width = track.clientWidth || 1;
          setActive(Math.round(track.scrollLeft / width));
        });
      },
      { passive: true }
    );

    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => {
        const slide = slides[i];
        if (slide) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        setActive(i);
        centerThumb(thumb);
      });
    });

    setActive(0, { scrollThumb: false });
  });
}

/* ---------------- Ships-by card ---------------- */
const GEO_KEY = 'rx-geo-country';
const DATE_OPTIONS = { weekday: 'short', month: 'short', day: 'numeric' };

function parseRange(value) {
  const match = String(value || '').match(/(\d+)\s*[-–]\s*(\d+)/) || String(value || '').match(/(\d+)/);
  if (!match) return null;
  const a = Number(match[1]);
  const b = Number(match[2] ?? match[1]);
  return [Math.min(a, b), Math.max(a, b)];
}

function addBusinessDays(start, days) {
  const date = new Date(start);
  let added = 0;
  while (added < days) {
    date.setDate(date.getDate() + 1);
    const weekday = date.getDay();
    if (weekday !== 0 && weekday !== 6) added += 1;
  }
  return date;
}

async function detectCountry(fallback) {
  try {
    const cached = sessionStorage.getItem(GEO_KEY);
    if (cached) return cached;
  } catch (_) {}

  try {
    const root = window.Shopify?.routes?.root || '/';
    const response = await fetch(
      `${root}browsing_context_suggestions.json?country[enabled]=true&currency[enabled]=true&language[enabled]=true`,
      { headers: { Accept: 'application/json' }, credentials: 'same-origin' }
    );
    if (response.ok) {
      const data = await response.json();
      const detected = data?.detected_values || {};
      const code = String(detected.country?.handle || detected.country_code || '').toUpperCase();
      if (/^[A-Z]{2}$/.test(code)) {
        try {
          sessionStorage.setItem(GEO_KEY, code);
        } catch (_) {}
        return code;
      }
    }
  } catch (_) {}

  return fallback;
}

/** Active language: Weglot first, then <html lang>, then the browser. */
function currentLang() {
  try {
    const weglot = window.Weglot;
    if (weglot && typeof weglot.getCurrentLang === 'function') {
      const code = weglot.getCurrentLang();
      if (code) return code;
    }
  } catch (_) {}
  return document.documentElement.lang || navigator.language || 'en';
}

function regionName(country, lang) {
  try {
    return new Intl.DisplayNames([lang, 'en'], { type: 'region' }).of(country) || country;
  } catch (_) {}
  try {
    return new Intl.DisplayNames(['en'], { type: 'region' }).of(country) || country;
  } catch (_) {}
  return country;
}

function dateFormatter(lang) {
  try {
    return new Intl.DateTimeFormat([lang, 'en'], DATE_OPTIONS);
  } catch (_) {
    return new Intl.DateTimeFormat('en', DATE_OPTIONS);
  }
}

function renderShipsBy(blocks, country, flags, lang) {
  const countryName = regionName(country, lang);
  const dateFormat = dateFormatter(lang);

  blocks.forEach((block) => {
    const markets = String(block.dataset.markets || '').split(',');
    if (!country || !markets.includes(country)) return;

    const days = (parseRange(block.dataset.processing) || [3, 3])[1];
    const shipBy = addBusinessDays(new Date(), days);

    const estimate = block.querySelector('[data-rx-delivery-estimate]');
    const fallback = block.querySelector('[data-rx-delivery-fallback]');
    const countryEl = block.querySelector('[data-rx-delivery-country]');
    const dateEl = block.querySelector('[data-rx-delivery-date]');
    const flagEl = block.querySelector('[data-rx-delivery-flag]');
    if (!estimate || !countryEl || !dateEl) return;

    countryEl.textContent = countryName;
    dateEl.textContent = dateFormat.format(shipBy);
    if (flagEl && flags[country]) flagEl.src = flags[country];

    estimate.hidden = false;
    if (fallback) fallback.hidden = true;
  });
}

/** Calls back once Weglot is ready and on every language switch. Gives up after ~10s without Weglot. */
function onWeglotLanguage(callback) {
  let tries = 0;
  const bind = () => {
    const weglot = window.Weglot;
    if (!weglot || typeof weglot.on !== 'function') {
      if (++tries < 40) setTimeout(bind, 250);
      return;
    }
    try {
      weglot.on('languageChanged', (lang) => callback(lang || currentLang()));
      if (weglot.initialized) callback(currentLang());
      else weglot.on('initialized', () => callback(currentLang()));
    } catch (_) {}
  };
  bind();
}

async function initShipsBy() {
  const blocks = document.querySelectorAll('[data-rx-delivery]');
  if (!blocks.length) return;

  let flags = {};
  try {
    flags = JSON.parse(document.querySelector('script[data-rx-flags]')?.textContent || '{}');
  } catch (_) {}

  const fallbackCountry = String(blocks[0].dataset.country || '').toUpperCase();
  const country = await detectCountry(fallbackCountry);

  renderShipsBy(blocks, country, flags, currentLang());
  onWeglotLanguage((lang) => renderShipsBy(blocks, country, flags, lang));
}

initGallery();
initStickyAtc();
initShipsBy();
